/**
 * Admin Coins/Wallets Management Tab Component
 * 
 * C1: Admin UI for managing supported coins and wallet addresses.
 */

import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  listCryptoAssets,
  createCryptoAsset,
  updateCryptoAsset,
  deactivateCryptoAsset,
  activateCryptoAsset,
  listCryptoWallets,
  createCryptoWallet,
  updateCryptoWallet,
  deactivateCryptoWallet,
  type CryptoAsset,
  type CryptoWallet,
  type CreateCryptoAssetRequest,
  type UpdateCryptoAssetRequest,
  type CreateCryptoWalletRequest,
  type UpdateCryptoWalletRequest,
} from "@app/services/adminPaymentsApi";
import { logger } from "@app/services/logger";
import "./admin-coins-wallets.css";

const ASSETS_QUERY_KEY = ["admin", "payments", "crypto", "assets"];
const WALLETS_QUERY_KEY = ["admin", "payments", "crypto", "wallets"];

function EditAssetModal({
  asset,
  onClose,
  onSuccess,
}: {
  asset: CryptoAsset;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<UpdateCryptoAssetRequest>({
    network: asset.network,
    token_contract: asset.token_contract || null,
    decimals: asset.decimals,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCryptoAssetRequest) => updateCryptoAsset(asset.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: unknown) => {
      logger.error("Failed to update asset:", error instanceof Error ? error : new Error(String(error)));
      let errorMessage = "Failed to update asset";
      
      if (error instanceof Error) {
        errorMessage = error.message;
        if ("response" in error && error.response && typeof error.response === "object") {
          const response = error.response as { data?: { detail?: unknown } };
          if (response.data?.detail) {
            const detail = response.data.detail;
            if (Array.isArray(detail)) {
              const messages = detail
                .map((err: { msg?: string; loc?: unknown[]; type?: string }) => {
                  const field = Array.isArray(err.loc) ? err.loc.slice(-1)[0] : "field";
                  return `${field}: ${err.msg || err.type || "Invalid value"}`;
                })
                .join("\n");
              errorMessage = `Validation errors:\n${messages}`;
            } else if (typeof detail === "string") {
              errorMessage = detail;
            }
          }
        }
      }
      
      alert(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prepare update data - only include fields that have changed
    const updateData: UpdateCryptoAssetRequest = {};
    
    // Network
    if (form.network !== undefined && form.network !== asset.network) {
      updateData.network = form.network;
    }
    
    // Token Contract - handle deletion explicitly
    const currentContract = asset.token_contract || null;
    const newContract = form.token_contract?.trim() || null;
    
    // If user wants to delete (empty string becomes null), or if it changed
    if (newContract !== currentContract) {
      updateData.token_contract = newContract;
    }
    
    // Decimals
    if (form.decimals !== undefined && form.decimals !== asset.decimals) {
      updateData.decimals = form.decimals;
    }
    
    // Validate Token Contract if provided
    if (updateData.token_contract && updateData.token_contract.trim()) {
      const contract = updateData.token_contract.trim();
      if (!contract.startsWith("0x") || contract.length !== 42) {
        alert("Token Contract must be a valid Ethereum address (0x followed by 40 hex characters) or leave empty for native tokens");
        return;
      }
      try {
        const hexPart = contract.slice(2);
        if (!/^[0-9a-fA-F]{40}$/.test(hexPart)) {
          alert("Token Contract must contain only hexadecimal characters (0-9, a-f, A-F)");
          return;
        }
      } catch {
        alert("Invalid Token Contract format");
        return;
      }
    }
    
    // Only send update if there are changes
    if (Object.keys(updateData).length === 0) {
      alert("No changes to save");
      return;
    }
    
    updateMutation.mutate(updateData);
  };

  return (
    <div className="coins-wallets-modal-overlay" onClick={onClose}>
      <div className="coins-wallets-modal" onClick={(e) => e.stopPropagation()}>
        <Card title={`Edit ${asset.symbol}`}>
          <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "var(--color-background-secondary, rgba(15, 23, 42, 0.5))", borderRadius: "0.5rem", fontSize: "0.875rem" }}>
            <strong>Edit Crypto Asset</strong>
            <p style={{ margin: "0.5rem 0 0 0", color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))" }}>
              Update the network, token contract, or decimals for {asset.symbol}. Note: Symbol cannot be changed.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="coins-wallets-form">
            <label>
              Symbol (read-only)
              <input
                type="text"
                value={asset.symbol}
                disabled
                style={{ opacity: 0.6, cursor: "not-allowed" }}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                Symbol cannot be changed after creation
              </small>
            </label>
            <label>
              Network *
              <input
                type="text"
                value={form.network || ""}
                onChange={(e) => setForm({ ...form, network: e.target.value })}
                placeholder="ethereum"
                required
                maxLength={32}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                The blockchain network (e.g., ethereum, bsc, polygon)
              </small>
            </label>
            <label>
              Token Contract (leave empty for native tokens)
              <input
                type="text"
                value={form.token_contract || ""}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setForm({
                    ...form,
                    token_contract: value || null,
                  });
                }}
                placeholder="0x... (Ethereum address, 42 characters)"
                maxLength={66}
                pattern="^(0x[a-fA-F0-9]{40})?$"
                title="Must be a valid Ethereum address starting with 0x followed by 40 hex characters, or leave empty for native tokens"
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                The smart contract address for tokens. Leave empty for native tokens like ETH.
              </small>
            </label>
            <label>
              Decimals *
              <input
                type="number"
                value={form.decimals || 0}
                onChange={(e) =>
                  setForm({ ...form, decimals: parseInt(e.target.value) || 0 })
                }
                min={0}
                max={18}
                required
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                Number of decimal places (USDC/USDT = 6, ETH = 18)
              </small>
            </label>
            <div className="coins-wallets-form-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function CreateAssetModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<CreateCryptoAssetRequest>({
    symbol: "",
    network: "",
    token_contract: null,
    decimals: 6,
  });

  const createMutation = useMutation({
    mutationFn: createCryptoAsset,
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: unknown) => {
      logger.error("Failed to create asset:", error instanceof Error ? error : new Error(String(error)));
      let errorMessage = "Failed to create asset";
      
      if (error instanceof Error) {
        errorMessage = error.message;
        // Extract validation error details from axios error response
        if ("response" in error && error.response && typeof error.response === "object") {
          const response = error.response as { data?: { detail?: unknown } };
          if (response.data?.detail) {
            const detail = response.data.detail;
            if (Array.isArray(detail)) {
              // FastAPI validation errors format
              const messages = detail
                .map((err: { msg?: string; loc?: unknown[]; type?: string }) => {
                  const field = Array.isArray(err.loc) ? err.loc.slice(-1)[0] : "field";
                  return `${field}: ${err.msg || err.type || "Invalid value"}`;
                })
                .join("\n");
              errorMessage = `Validation errors:\n${messages}`;
            } else if (typeof detail === "string") {
              errorMessage = detail;
            }
          }
        }
      }
      
      alert(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation for Token Contract
    if (form.token_contract && form.token_contract.trim()) {
      const contract = form.token_contract.trim();
      // Validate Ethereum address format
      if (!contract.startsWith("0x") || contract.length !== 42) {
        alert("Token Contract must be a valid Ethereum address (0x followed by 40 hex characters) or leave empty for native tokens");
        return;
      }
      // Validate hex characters
      try {
        const hexPart = contract.slice(2);
        if (!/^[0-9a-fA-F]{40}$/.test(hexPart)) {
          alert("Token Contract must contain only hexadecimal characters (0-9, a-f, A-F)");
          return;
        }
      } catch {
        alert("Invalid Token Contract format");
        return;
      }
    }
    
    createMutation.mutate(form);
  };

  return (
    <div className="coins-wallets-modal-overlay" onClick={onClose}>
      <div className="coins-wallets-modal" onClick={(e) => e.stopPropagation()}>
        <Card title="Create Crypto Asset">
          <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "var(--color-background-secondary, rgba(15, 23, 42, 0.5))", borderRadius: "0.5rem", fontSize: "0.875rem" }}>
            <strong>What is a Crypto Asset?</strong>
            <p style={{ margin: "0.5rem 0 0 0", color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))" }}>
              A crypto asset is a cryptocurrency that users can pay with (e.g., USDC, USDT, ETH). 
              You need to define the coin's symbol, network, and token contract address (if it's a token, not native).
            </p>
          </div>
          <form onSubmit={handleSubmit} className="coins-wallets-form">
            <label>
              Symbol *
              <input
                type="text"
                value={form.symbol}
                onChange={(e) =>
                  setForm({ ...form, symbol: e.target.value.toUpperCase() })
                }
                placeholder="USDT"
                required
                maxLength={16}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                The coin symbol (e.g., USDT, USDC, ETH)
              </small>
            </label>
            <label>
              Network *
              <input
                type="text"
                value={form.network}
                onChange={(e) => setForm({ ...form, network: e.target.value })}
                placeholder="ethereum"
                required
                maxLength={32}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                The blockchain network (e.g., ethereum, bsc, polygon)
              </small>
            </label>
            <label>
              Token Contract (leave empty for native tokens)
              <input
                type="text"
                value={form.token_contract || ""}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setForm({
                    ...form,
                    token_contract: value || null,
                  });
                }}
                placeholder="0x... (Ethereum address, 42 characters)"
                maxLength={66}
                pattern="^(0x[a-fA-F0-9]{40})?$"
                title="Must be a valid Ethereum address starting with 0x followed by 40 hex characters, or leave empty for native tokens"
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                The smart contract address for tokens (e.g., USDC contract on Ethereum). Leave empty for native tokens like ETH.
              </small>
            </label>
            <label>
              Decimals *
              <input
                type="number"
                value={form.decimals}
                onChange={(e) =>
                  setForm({ ...form, decimals: parseInt(e.target.value) || 0 })
                }
                min={0}
                max={18}
                required
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                Number of decimal places (USDC/USDT = 6, ETH = 18)
              </small>
            </label>
            <div className="coins-wallets-form-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function CreateWalletModal({
  assets,
  onClose,
  onSuccess,
}: {
  assets: CryptoAsset[] | undefined;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<CreateCryptoWalletRequest>({
    asset_id: "",
    address: "",
    label: null,
  });

  // Filter active assets
  const activeAssets = assets?.filter((a) => a.is_active) || [];

  const createMutation = useMutation({
    mutationFn: createCryptoWallet,
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: unknown) => {
      logger.error("Failed to create wallet:", error instanceof Error ? error : new Error(String(error)));
      alert(error instanceof Error ? error.message : "Failed to create wallet");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  return (
    <div className="coins-wallets-modal-overlay" onClick={onClose}>
      <div className="coins-wallets-modal" onClick={(e) => e.stopPropagation()}>
        <Card title="Create Crypto Wallet">
          <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "var(--color-background-secondary, rgba(15, 23, 42, 0.5))", borderRadius: "0.5rem", fontSize: "0.875rem" }}>
            <strong>What is a Wallet Address?</strong>
            <p style={{ margin: "0.5rem 0 0 0", color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))" }}>
              A wallet address is where you receive payments from users. 
              You need to add a wallet address for each coin you want to accept payments for.
              This is the address users will send their payments to.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="coins-wallets-form">
            <label>
              Coin *
              <select
                value={form.asset_id}
                onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
                required
                disabled={activeAssets.length === 0}
              >
                <option value="">-- Select a coin --</option>
                {activeAssets.length === 0 ? (
                  <option value="" disabled>
                    No active coins available. Please activate a coin first.
                  </option>
                ) : (
                  activeAssets.map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {asset.symbol} on {asset.network}
                    </option>
                  ))
                )}
              </select>
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                {activeAssets.length === 0
                  ? "No active coins available. Please activate a coin in the 'Supported Coins' section above first."
                  : "Select which coin this wallet will receive payments for"}
              </small>
            </label>
            <label>
              Wallet Address *
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value.trim() })}
                placeholder="0x... (Your wallet address to receive payments)"
                required
                maxLength={66}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                This is YOUR wallet address where users will send payments. Make sure you control this wallet!
              </small>
            </label>
            <label>
              Label (optional)
              <input
                type="text"
                value={form.label || ""}
                onChange={(e) =>
                  setForm({ ...form, label: e.target.value || null })
                }
                placeholder="Main wallet, Hot wallet, etc."
                maxLength={128}
              />
              <small style={{ color: "var(--color-text-secondary, rgba(226, 232, 240, 0.7))", fontSize: "0.75rem" }}>
                A friendly name to identify this wallet (e.g., "Main USDC Wallet", "Hot Wallet")
              </small>
            </label>
            <div className="coins-wallets-form-actions">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

export function AdminCoinsWalletsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [showCreateAsset, setShowCreateAsset] = useState(false);
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [editingAsset, setEditingAsset] = useState<CryptoAsset | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [qrModalAddress, setQrModalAddress] = useState<string | null>(null);

  const { data: assets, isLoading: assetsLoading } = useQuery<CryptoAsset[]>({
    queryKey: ASSETS_QUERY_KEY,
    queryFn: listCryptoAssets,
  });

  const { data: wallets, isLoading: walletsLoading } = useQuery<CryptoWallet[]>({
    queryKey: [...WALLETS_QUERY_KEY, selectedAssetId],
    queryFn: () => listCryptoWallets(selectedAssetId || undefined),
  });

  const deactivateAssetMutation = useMutation({
    mutationFn: deactivateCryptoAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
    },
  });

  const activateAssetMutation = useMutation({
    mutationFn: activateCryptoAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
    },
  });

  const updateAssetMutation = useMutation({
    mutationFn: ({ assetId, data }: { assetId: string; data: UpdateCryptoAssetRequest }) =>
      updateCryptoAsset(assetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
    },
  });

  const deactivateWalletMutation = useMutation({
    mutationFn: deactivateCryptoWallet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
    },
  });

  const handleToggleAsset = (asset: CryptoAsset) => {
    if (asset.is_active) {
      if (
        confirm(
          `Are you sure you want to deactivate ${asset.symbol}? This will disable all wallets for this asset.`,
        )
      ) {
        deactivateAssetMutation.mutate(asset.id);
      }
    } else {
      if (confirm(`Are you sure you want to activate ${asset.symbol}?`)) {
        activateAssetMutation.mutate(asset.id);
      }
    }
  };

  const handleDeactivateWallet = (wallet: CryptoWallet) => {
    if (confirm(`Are you sure you want to deactivate wallet ${wallet.address}?`)) {
      deactivateWalletMutation.mutate(wallet.id);
    }
  };

  return (
    <div className="admin-coins-wallets-root">
      <SectionHeader
        title="Coins & Wallets Management"
        subtitle="Manage supported cryptocurrencies and wallet addresses for payments."
      />

      {/* Assets Section */}
      <Card
        title="Supported Coins"
        subtitle="Add cryptocurrencies that users can pay with (e.g., USDC, USDT). Each coin needs a symbol, network, and token contract address (if it's a token, not native)."
        actions={
          <Button variant="primary" onClick={() => setShowCreateAsset(true)}>
            Add Coin
          </Button>
        }
      >
        {assetsLoading ? (
          <div className="coins-wallets-loading">Loading assets...</div>
        ) : !assets || assets.length === 0 ? (
          <div className="coins-wallets-empty">No assets found. Add the first asset to get started.</div>
        ) : (
          <table className="coins-wallets-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Network</th>
                <th>Token Contract</th>
                <th>Decimals</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <strong>{asset.symbol}</strong>
                  </td>
                  <td>{asset.network}</td>
                  <td className="coins-wallets-code">
                    {asset.token_contract || "Native"}
                  </td>
                  <td>{asset.decimals}</td>
                  <td>
                    <Pill tone={asset.is_active ? "success" : "danger"}>
                      {asset.is_active ? "Active" : "Inactive"}
                    </Pill>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() => setEditingAsset(asset)}
                        disabled={updateAssetMutation.isPending}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleToggleAsset(asset)}
                        disabled={deactivateAssetMutation.isPending || activateAssetMutation.isPending}
                      >
                        {asset.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Wallets Section */}
      <Card
        title="Wallet Addresses"
        subtitle="Add wallet addresses to receive payments for each coin. Users will send payments to these addresses. You can add multiple wallets per coin."
        actions={
          <Button variant="primary" onClick={() => setShowCreateWallet(true)}>
            Add Wallet
          </Button>
        }
      >
        <div className="coins-wallets-filters">
          <label>
            Filter by Asset
            <select
              value={selectedAssetId || ""}
              onChange={(e) => setSelectedAssetId(e.target.value || null)}
            >
              <option value="">All Assets</option>
              {assets
                ?.filter((a) => a.is_active)
                .map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.symbol}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {walletsLoading ? (
          <div className="coins-wallets-loading">Loading wallets...</div>
        ) : !wallets || wallets.length === 0 ? (
          <div className="coins-wallets-empty">No wallets found. Add the first wallet to get started.</div>
        ) : (
          <table className="coins-wallets-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Address</th>
                <th>Label</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map((wallet) => (
                <tr key={wallet.id}>
                  <td>
                    <strong>{wallet.asset_symbol}</strong>
                  </td>
                  <td className="coins-wallets-code">
                    <span className="coins-wallets-address-row">
                      {wallet.address}
                      <button
                        type="button"
                        className="coins-wallets-qr-btn"
                        onClick={() => setQrModalAddress(wallet.address)}
                        title="Show QR code"
                        aria-label="Show QR code"
                      >
                        QR
                      </button>
                    </span>
                  </td>
                  <td>{wallet.label || "-"}</td>
                  <td>
                    <Pill tone={wallet.is_active ? "success" : "danger"}>
                      {wallet.is_active ? "Active" : "Inactive"}
                    </Pill>
                  </td>
                  <td>{new Date(wallet.created_at).toLocaleDateString("en-US")}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => handleDeactivateWallet(wallet)}
                      disabled={deactivateWalletMutation.isPending}
                    >
                      {wallet.is_active ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showCreateAsset && (
        <CreateAssetModal
          onClose={() => setShowCreateAsset(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            setShowCreateAsset(false);
          }}
        />
      )}

      {editingAsset && (
        <EditAssetModal
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
            setEditingAsset(null);
          }}
        />
      )}

      {showCreateWallet && assets && (
        <CreateWalletModal
          assets={assets}
          onClose={() => setShowCreateWallet(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: WALLETS_QUERY_KEY });
            setShowCreateWallet(false);
          }}
        />
      )}

      {qrModalAddress && (
        <div className="modal-overlay" onClick={() => setQrModalAddress(null)}>
          <div className="modal-card qr-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: "1rem", textAlign: "center" }}>Wallet QR Code</h3>
            <div className="qr-modal-content">
              <QRCodeSVG
                value={qrModalAddress}
                size={220}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={true}
              />
            </div>
            <code className="qr-modal-address">{qrModalAddress}</code>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setQrModalAddress(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
